import React, { useState, useEffect } from "react";
import { compose, withAppContext, withActions } from "containers/HOC";
import { Form, Field } from "react-final-form";
import { FORM_ERROR } from "final-form";
import { Subject } from "rxjs";
import Modal from "components/Modal/Modal";
import ModalBody from "components/Modal/ModalBody";
import ModalBodyAlert from "components/Modal/ModalBodyAlert";
import ModalFooter from "components/Modal/ModalFooter";
import ModalFooterLeft from "components/Modal/ModalFooterLeft";
import ModalFooterRight from "components/Modal/ModalFooterRight";
import Button from "components/Button/Button";
import Grid from "components/Grid";
import GridCell from "components/GridCell";
import SearchInputPack from "components/SearchInputPack";
import TextareaPack from "components/TextareaPack";
import Alert from "components/Alert";
import SortableTable from "components/new/SortableTable";
import SortableTableHeader from "components/new/SortableTableHeader";
import { findFarms, inviteFarmGroupMembers } from "./_actions";

const onSearch$ = new Subject();

const handleSearchTextChange = (previousSearchResults, selectedFarmIds) => (e) => {
    const searchText = e.target.value;
    onSearch$.next({ searchText, previousSearchResults, selectedFarmIds });
};

const handleClearSearch = (form) => (e) => {
    form.change("search", "");
};

const submit = (inviteFarmGroupMembers, close) => async (farmGroup) => {
    const submitResult = await inviteFarmGroupMembers(farmGroup.id, farmGroup.farmIds, farmGroup.comments)
        .then((response) => close())
        .catch((error) => ({ [FORM_ERROR]: error }));

    return submitResult;
};

const InviteFarmGroupMembersModal = ({ farmGroup, findFarms, inviteFarmGroupMembers, close }) => {
    const [searchResults, setSearchResults] = useState([]);

    useEffect(() => {
        async function handleFindFarms(searchText, previousSearchResults, selectedFarmIds) {
            const newSearchResults = await findFarms(searchText)
                .then((response) => response)
                .catch((error) => []);

            const selectedFarms = previousSearchResults.filter((sr) => selectedFarmIds.includes(sr.id));
            const newFarms = newSearchResults.filter((s) => !selectedFarmIds.includes(s.id));

            setSearchResults([...selectedFarms, ...newFarms]);
        }

        // Like componentDidMount
        const subscription = onSearch$.debounceTime(500).subscribe((searchParams) => handleFindFarms(searchParams.searchText, searchParams.previousSearchResults, searchParams.selectedFarmIds));

        // Like componentWillUnmount
        return () => subscription.unsubscribe();
    }, [findFarms]);

    return (
        <Form initialValues={farmGroup} onSubmit={submit(inviteFarmGroupMembers, close)}>
            {({ form, handleSubmit, pristine, submitting, submitError, dirtySinceLastSubmit, values }) => {
                const selectedFarmIds = values.farmIds || [];

                const disableSubmit = pristine || submitting || selectedFarmIds.length === 0;

                return (
                    <form onSubmit={handleSubmit}>
                        <Modal title={`Invite new members to join '${farmGroup.name}'`} close={close} submitting={submitting}>
                            <ModalBody>
                                <ModalBodyAlert>{!dirtySinceLastSubmit && submitError && <Alert type="error" text={submitError} />}</ModalBodyAlert>
                                <Grid title="Find farms to invite">
                                    <GridCell className="u-lg-width2of3">
                                        <div className="Field u-mt-md">
                                            <label className="Field-label">Farm search</label>
                                            <Field name="search" placeholder="Search by farm name or address" onChange={handleSearchTextChange(searchResults, selectedFarmIds)} onClear={handleClearSearch(form)} component={SearchInputPack} />
                                        </div>
                                    </GridCell>
                                </Grid>
                                <Grid className="u-mt-md">
                                    <GridCell>
                                        <span className="h3">{searchResults.length}</span> farm(s) found
                                    </GridCell>
                                </Grid>
                                {searchResults.length > 0 && (
                                    <Grid className="u-mt-md">
                                        <GridCell>
                                            <SortableTable data={searchResults} defaultSortCriteria={{ fieldName: "name", fieldType: "string" }}>
                                                {({ data, sortCriteria, handleSort }) => {
                                                    return (
                                                        <table>
                                                            <thead>
                                                                <tr>
                                                                    <th className="th--shrink"></th>
                                                                    <SortableTableHeader label="Name" fieldName="name" sortCriteria={sortCriteria} handleSort={handleSort} />
                                                                    <SortableTableHeader label="Address" fieldName="address" sortCriteria={sortCriteria} handleSort={handleSort} />
                                                                    <SortableTableHeader label="Ownership status" fieldName="ownershipStatus" sortCriteria={sortCriteria} handleSort={handleSort} />
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {data.map((farm) => {
                                                                    const isAlreadyAMember = (farmGroup.memberFarms || []).some((m) => m.farmId === farm.id);
                                                                    const isAlreadyInvited = (farmGroup.invitedFarms || []).some((i) => i.farmId === farm.id);
                                                                    const canBeSelected = !isAlreadyAMember && !isAlreadyInvited;
                                                                    return (
                                                                        <tr key={farm.id} className={canBeSelected ? "hover" : ""}>
                                                                            <td className="u-textCenter">
                                                                                {isAlreadyAMember && <span>Member</span>}
                                                                                {isAlreadyInvited && <span>Invited</span>}
                                                                                {canBeSelected && (
                                                                                    <label className="Checkbox">
                                                                                        <Field name="farmIds" id={farm.id} data-testing-name={farm.name} value={farm.id} className="Checkbox-input" type="checkbox" component="input" />
                                                                                        <span className="Checkbox-label"></span>
                                                                                    </label>
                                                                                )}
                                                                            </td>
                                                                            <td>{farm.name}</td>
                                                                            <td>{farm.address}</td>
                                                                            <td>{farm.ownershipStatus}</td>
                                                                        </tr>
                                                                    );
                                                                })}
                                                            </tbody>
                                                        </table>
                                                    );
                                                }}
                                            </SortableTable>
                                        </GridCell>
                                        <GridCell>
                                            <Field name="comments" label="Invitation comments" placeholder="Enter any comments to include on the invitation email to the invited farms" type="text" component={TextareaPack} />
                                        </GridCell>
                                    </Grid>
                                )}
                            </ModalBody>
                            <ModalFooter>
                                <ModalFooterLeft>
                                    <Button id="btnFarmGroupInvite_cancel" onClick={close} secondary disabled={submitting}>
                                        Cancel
                                    </Button>
                                </ModalFooterLeft>
                                <ModalFooterRight>
                                    <Button id="btnFarmGroupInvite_send" submit primary disabled={disableSubmit}>
                                        Send
                                    </Button>
                                </ModalFooterRight>
                            </ModalFooter>
                        </Modal>
                    </form>
                );
            }}
        </Form>
    );
};
export default compose(withAppContext, withActions({ findFarms, inviteFarmGroupMembers }))(InviteFarmGroupMembersModal);
